/**
 * Marketing → Konkurrenter ([D-292], Charlie 14-09-2026).
 *
 * Deles af server (skema-værdier, agentens regler, API-svar) og web (listen,
 * detaljesiden, Overvåg-knappen på Organic search). Alt hvad BEGGE sider skal
 * være enige om står her: statusser, oprindelser, produkttags og DTO'erne.
 *
 * ÆRLIGHED ([D-107]): en konkurrents omsætning oplyses IKKE i regnskabsklasse B
 * (alle på listen), så omsætning er altid et ~estimat, og estimatets grundlag
 * står i `RevenueBasis`. Trafik er udbyderens ESTIMAT (DataForSEO), ordrer og
 * annoncespend er afledt af VORES egne målte rater — det er benchmarkens
 * præmis, ikke en måling af konkurrenten.
 */

/** Vores eget selskab — nøglen til samme kilder som konkurrenterne læses fra. */
export const OWN_COMPETITOR_CVR = '32059279';
export const OWN_COMPETITOR_DOMAIN = 'badogfliser.dk';
export const OWN_COMPETITOR_NAME = 'Bad & Fliser A/S';

/**
 * 'overvaaget'  = på listen, måles hver nat.
 * 'foreslaaet'  = agenten har fundet den; et menneske skal sige ja/nej.
 * 'udelukket'   = fjernet OG spærret: agenten foreslår den aldrig igen.
 */
export const COMPETITOR_STATUSES = ['overvaaget', 'foreslaaet', 'udelukket'] as const;
export type CompetitorStatus = (typeof COMPETITOR_STATUSES)[number];

/**
 * Hvem satte konkurrenten på listen. 'serp' = Overvåg-knappen på Organic
 * search (et menneske, men fra SERP-listen); 'agent' = natagentens forslag.
 */
export const COMPETITOR_ORIGINS = ['manuel', 'agent', 'serp'] as const;
export type CompetitorOrigin = (typeof COMPETITOR_ORIGINS)[number];

/** Rullende vinduer i måneder (Charlie: 1m, 3m, 6m, 12m). */
export const COMPETITOR_WINDOWS = [1, 3, 6, 12] as const;
export type CompetitorWindow = (typeof COMPETITOR_WINDOWS)[number];

/**
 * Produkttags — med afsæt i husets egne produkttyper (BC's varebogføringsgrupper,
 * fx FLISER_EX). `bcCodes` er de grupper tagget dækker hos os, så et tag senere
 * kan kobles til vores egen omsætning pr. kategori. `ours: false` = et
 * sortiment vi IKKE fører; det er vigtigt at kunne se, fordi en konkurrents
 * regnskab dækker HELE deres forretning.
 */
export interface CompetitorProductTag {
  key: string;
  label: string;
  bcCodes: string[];
  ours: boolean;
}

export const COMPETITOR_PRODUCT_TAGS: readonly CompetitorProductTag[] = [
  { key: 'FLISER_EX', label: 'Fliser og klinker', bcCodes: ['FLISER', 'FLISER_EX', 'FLI-DIV_EX'], ours: true },
  { key: 'ST-FORM_EX', label: 'Storformat', bcCodes: ['ST-FORM_EX', 'ST-FO_2_EX', 'ST-FO_3_EX', 'STORFORMAT'], ours: true },
  { key: 'MOSAIK', label: 'Mosaik', bcCodes: ['MOSAIK', 'MOS_EX', 'MOSAIK_EX'], ours: true },
  { key: 'TERRAZZO', label: 'Terrazzo', bcCodes: ['TERRAZZO', 'TERRAZZ_EX', 'TER-DIV_EX'], ours: true },
  { key: 'NATURSTEN', label: 'Natursten', bcCodes: ['NATURSTEN', 'NATURST_EX', 'NAT-DIV_EX'], ours: true },
  { key: 'MØBLER_EX', label: 'Badeværelsesmøbler', bcCodes: ['MØBLER_EX', 'DANSANI', 'INDRET_EX'], ours: true },
  { key: 'ARMATUR_EX', label: 'Armaturer og brusesæt', bcCodes: ['ARMATUR_EX', 'BRUSSÆT_EX'], ours: true },
  { key: 'VASKE_EX', label: 'Håndvaske', bcCodes: ['VASKE_EX'], ours: true },
  { key: 'TOILET_EX', label: 'Toiletter', bcCodes: ['TOILET_EX'], ours: true },
  { key: 'BADEKAR_EX', label: 'Badekar', bcCodes: ['BADEKAR_EX'], ours: true },
  { key: 'AFSKÆRM_EX', label: 'Bruseafskærmning', bcCodes: ['AFSKÆRM_EX'], ours: true },
  { key: 'SPEJLE_EX', label: 'Spejle', bcCodes: ['SPEJLE_EX'], ours: true },
  { key: 'HÅKLTØ_EX', label: 'Håndklædetørrere', bcCodes: ['HÅKLTØ_EX', 'HF_EX'], ours: true },
  { key: 'TILBEHØ_EX', label: 'Tilbehør', bcCodes: ['TILBEHØ_EX', 'TILBEHØR', 'PLEJEMI_EX'], ours: true },
  { key: 'VVS_EX', label: 'VVS', bcCodes: ['VVS', 'VVS_EX'], ours: true },
  { key: 'BYGMAT_EX', label: 'Byggematerialer, fuge og værktøj', bcCodes: ['BYGMAT_EX', 'BYGGEMATR', 'KLÆBE/FUGE', 'FUGESIL_EX', 'PLADER', 'VÆRKTØJ'], ours: true },
  // Sortimenter vi ikke fører. Står med, fordi de forklarer en del af
  // konkurrentens regnskab som IKKE konkurrerer med os.
  { key: 'KØKKEN', label: 'Køkkener', bcCodes: [], ours: false },
  { key: 'GULVE', label: 'Trægulve, laminat og vinyl', bcCodes: [], ours: false },
  { key: 'UDENDØRS', label: 'Udendørs belægning', bcCodes: [], ours: false },
  { key: 'TOTALRENOVERING', label: 'Totalrenovering og håndværk', bcCodes: [], ours: false },
  { key: 'MØBLER', label: 'Møbler til hjemmet', bcCodes: [], ours: false },
];

export const COMPETITOR_PRODUCT_TAG_KEYS: readonly string[] = COMPETITOR_PRODUCT_TAGS.map((t) => t.key);

export function competitorTagLabel(key: string): string {
  return COMPETITOR_PRODUCT_TAGS.find((t) => t.key === key)?.label ?? key;
}

/** Ét regnskabsår som Virk udstiller det (beløb i KR — det er hvad DTO'et bærer). */
export interface CompetitorFinancialYear {
  periodStart: string;
  periodEnd: string;
  /** Nettoomsætning. Næsten altid null: regnskabsklasse B oplyser den ikke. */
  revenueKr: number | null;
  grossProfitKr: number | null;
  ebitKr: number | null;
  depreciationKr: number | null;
  /** EBIT + af- og nedskrivninger. Null hvis et af leddene mangler. */
  ebitdaKr: number | null;
  profitBeforeTaxKr: number | null;
  profitKr: number | null;
  /** Gennemsnitligt antal beskæftigede (årsværk) i regnskabsåret. */
  avgEmployees: number | null;
  employeeExpenseKr: number | null;
  equityKr: number | null;
  reportingClass: string | null;
  publishedAt: string | null;
  documentUrl: string | null;
}

/** Ét rullende vindue: månedsgennemsnit over de seneste N HELE måneder. */
export interface CompetitorWindowMetrics {
  months: number;
  /** Hvor mange af de N måneder der faktisk har data. */
  monthsWithData: number;
  organicEtvPerMonth: number | null;
  paidEtvPerMonth: number | null;
  /** organisk + betalt. Udbyderens estimat, ikke en måling. */
  sessionsPerMonth: number | null;
  /** sessions × den CVR der gælder for rækken (vores egen, eller kalibreret). */
  ordersPerMonth: number | null;
  /** ordrer × vores AOV ex moms. */
  webRevenuePerMonth: number | null;
  /** betalt ETV × vores egen CPC i samme måneder. */
  adSpendPerMonth: number | null;
}

/** Grundlaget for ~Omsætning/år. */
export type RevenueBasis = 'regnskab' | 'bruttofortjeneste' | 'trafik' | 'ingen';

export interface CompetitorRowDto {
  id: number;
  domain: string;
  name: string;
  cvr: string | null;
  country: string;
  foundedYear: number | null;
  productTags: string[];
  status: CompetitorStatus;
  origin: CompetitorOrigin;
  suggestionReason: string | null;
  suggestionEvidence: Record<string, unknown> | null;
  knownOrdersPerYear: number | null;
  knownOrdersNote: string | null;
  note: string | null;
  createdAt: number;
  createdBy: string | null;
  decidedAt: number | null;
  decidedBy: string | null;
  excludedReason: string | null;
  masterSyncedAt: number | null;
  /** Seneste aflagte regnskabsår. Null = intet regnskab fundet (fx udenlandsk). */
  latest: CompetitorFinancialYear | null;
  /** Året før, til YoY på detaljesiden. */
  previous: CompetitorFinancialYear | null;
  /** ~Omsætning for seneste regnskabsår + hvad den hviler på. */
  revenueEstimateKr: number | null;
  revenueBasis: RevenueBasis;
  /** Pr. årsværk, seneste regnskabsår. */
  revenuePerFteKr: number | null;
  profitBeforeTaxPerFteKr: number | null;
  /** Rullende vinduer, nøgle = antal måneder. */
  windows: Record<string, CompetitorWindowMetrics>;
  /** Konverteringsraten rækken er regnet med, i %. */
  cvrPct: number | null;
  cvrBasis: 'egen' | 'kalibreret' | 'ingen';
  /** Antal af vores målte søgeord hvor domænet står i organisk top 10 (seneste opslag). */
  serpTop10Keywords: number | null;
  /** Antal af vores målte søgeord hvor domænet står i Shopping/produktgitteret (seneste opslag). */
  serpShoppingKeywords: number | null;
  /** Antal af vores målte søgeord hvor domænet står med en tekstannonce (seneste opslag). */
  serpPaidKeywords: number | null;
  /** Fælles søgeord fra DataForSEO's domæneliste (seneste øjebliksbillede). */
  sharedKeywords: number | null;
}

/** Vores egen række — samme felter, så DeltaPills kan regnes 1:1. */
export interface OwnCompetitorRowDto extends CompetitorRowDto {
  /** Målt CVR (samtykke-korrigeret) i det 12-måneders vindue benchmarken bruger. */
  measuredCvrPct: number | null;
  measuredAovKr: number | null;
  measuredCpcKr: number | null;
  /** Samtykkeandelen der er brugt til at opjustere GA4-sessions. */
  consentShare: number | null;
  /** Vinduet raterne er målt i. */
  measuredFrom: string | null;
  measuredTo: string | null;
}

export interface CompetitorSettingsDto {
  grossMarginPct: number;
  serpKeywords: number;
  minSharedKeywords: number;
  minSerpKeywordHits: number;
}

export interface CompetitorListResponse {
  ok: true;
  rows: CompetitorRowDto[];
  own: OwnCompetitorRowDto;
  counts: Record<CompetitorStatus, number>;
  /** Markedstrend: overvågede konkurrenters organiske trafik, seneste 3 hele mdr mod samme 3 mdr året før. */
  market: {
    monitored: number;
    organicEtvLast3m: number | null;
    organicEtvPriorYear3m: number | null;
    trendPct: number | null;
    ownTrendPct: number | null;
    /** Seneste hele måned i trafikserien. */
    latestMonth: string | null;
  };
  settings: CompetitorSettingsDto;
  freshness: {
    trafficSyncedAt: number | null;
    financialsSyncedAt: number | null;
    serpDate: string | null;
    serpKeywordCount: number;
  };
}

export interface CompetitorTrafficMonthDto {
  month: string;
  organicEtv: number | null;
  organicCount: number | null;
  paidEtv: number | null;
  paidCount: number | null;
}

export interface CompetitorSerpKeywordDto {
  keyword: string;
  /** Organisk placering (absolut) på seneste opslag, null = ikke i top 20. */
  organicRank: number | null;
  shopping: boolean;
  paid: boolean;
  /** VORES organiske placering på samme opslag. */
  ownRank: number | null;
}

export interface CompetitorDetailResponse {
  ok: true;
  row: CompetitorRowDto;
  own: OwnCompetitorRowDto;
  years: CompetitorFinancialYear[];
  ownYears: CompetitorFinancialYear[];
  traffic: CompetitorTrafficMonthDto[];
  ownTraffic: CompetitorTrafficMonthDto[];
  serp: { date: string | null; keywords: CompetitorSerpKeywordDto[] };
  settings: CompetitorSettingsDto;
}

/** Svar fra CVR-opslaget ved tilføjelse (cvrapi.dk). */
export interface CvrLookupResult {
  cvr: string;
  name: string;
  city: string | null;
  foundedYear: number | null;
  employees: number | null;
  industry: string | null;
  companyType: string | null;
  email: string | null;
}

/** Konkurrentens status som Organic search-listen skal kende den. */
export interface CompetitorWatchInfo {
  id: number;
  status: CompetitorStatus;
}

/** Domænet uden protokol, www og sti — én nøgle for samme site. */
export function normalizeCompetitorDomain(value: string): string {
  let v = value.trim().toLowerCase();
  v = v.replace(/^https?:\/\//, '').replace(/^www\./, '');
  v = v.split('/')[0] ?? v;
  v = v.split('?')[0] ?? v;
  return v;
}

export function isValidCompetitorDomain(value: string): boolean {
  return /^[a-z0-9æøå-]+(\.[a-z0-9æøå-]+)+$/.test(value);
}

export function isValidCvr(value: string): boolean {
  return /^\d{8}$/.test(value);
}
